"use client";

import { useEffect } from "react";
import { Stack, Divider, Box } from "@mui/material";
import QrHero from "@/sections/quantum-readiness/qrHero";
import QrUrgency from "@/sections/quantum-readiness/qrUrgency";
import QrGap from "@/sections/quantum-readiness/qrGap";
import QrFramework from "@/sections/quantum-readiness/qrFramework";
import QrProcess from "@/sections/quantum-readiness/qrProcess";
import QrResources from "@/sections/quantum-readiness/qrResources";
import QrScanCta from "@/sections/quantum-readiness/qrScanCta";
import Footer from "@/components/layout/footer";
import theme from "@/theme/theme";
import Image from "next/image";

import servicesPageGradient from "@/assets/images/servicesPage/servicesPageGradients.webp";

export default function QuantumReadinessPage() {

    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            const element = document.querySelector(hash);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 100);
            }
        }
    }, []);

    const scrollOffset = {
        scrollMarginTop: { xs: "70px", md: "120px" },
        width: '100%'
    };

    // Rhythm via spacing: sections form a narrative in three beats —
    // problem (Urgency · Gap), solution (Framework · Process), next steps
    // (Resources · ScanCta). Tight dividers group within a beat; a wider,
    // borderless gap separates the beats so the page breathes in threes.
    const sectionDivider = <Divider sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.17)", marginY: { xs: 5, lg: 8 }, marginX: "auto", width: "80%" }} />;
    const groupBreak = <Box sx={{ height: { xs: 40, lg: 88 }, width: "100%" }} />;

    return (
        <Stack width={"100%"} alignItems={"center"} gap={2}>
            <QrHero />
            <Stack width={"100%"} alignItems={"center"} position={"relative"} paddingBottom={10}>
                <Stack width={"100%"} height={"100%"} sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    zIndex: 1,
                }}>
                    <Image src={servicesPageGradient} alt="quantum readiness page gradient" width={1000} height={1000} style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        zIndex: 1,
                    }} />
                </Stack>
                <Stack width={"100%"} alignItems={"center"} position={"relative"} overflow={"hidden"} sx={{
                    zIndex: 2,
                    pt: { xs: "60px", md: "100px" },
                }}>
                    {/* Beat 1 — the problem */}
                    <Box id="urgency" sx={scrollOffset}>
                        <QrUrgency />
                    </Box>
                    {sectionDivider}
                    <Box id="readiness" sx={scrollOffset}>
                        <QrGap />
                    </Box>
                    {groupBreak}
                    {/* Beat 2 — the solution */}
                    <Box id="framework" sx={scrollOffset}>
                        <QrFramework />
                    </Box>
                    {sectionDivider}
                    <Box id="approach" sx={scrollOffset}>
                        <QrProcess />
                    </Box>
                    {groupBreak}
                    {/* Beat 3 — next steps */}
                    <Box id="resources" sx={scrollOffset}>
                        <QrResources />
                    </Box>
                    {sectionDivider}
                    <Box id="free-scan" sx={scrollOffset}>
                        <QrScanCta />
                    </Box>
                </Stack>
            </Stack>
            <Stack width={"100%"} alignItems={"center"} position={"relative"} sx={{
                borderTop: `1px solid ${theme.palette.brand.border1.main}`,
                zIndex: 2,
                backgroundColor: "#0B0B0B",
            }}>
                <Footer />
            </Stack>
        </Stack>
    );
}
